
import { NeuralProxy } from "./NeuralProxy";
import { Type } from "@google/genai";
import { AnalysisResult, AICoachSummary, SmartExercise, TransformationProtocol } from "../types";
import { GLOBAL_SEVERITY_MATRIX } from "./severityEngine";

/**
 * GSC AI COACHING SERVICE v18.0 - FORENSIC PEDAGOGY
 * Gemini agit comme un analyste de transfert : "Peux-tu le faire sans moi ?"
 */
export const getAICoachingAdvice = async (
    result: AnalysisResult, 
    lang: 'en' | 'fr' = 'fr',
    activeMission?: any,
    isPremium?: boolean
): Promise<{ summary: AICoachSummary }> => {
    const levelId = result.coaching_level || 4;
    const metrics = result.session.metrics;
    
    const prompt = `
        RÔLE : Expert en Didactique Musicale & Biomécanique.
        MISSION : Diagnostiquer la CAUSE PHYSIQUE de l'erreur et évaluer le potentiel de TRANSFERT.
        
        CONTEXTE :
        - Niveau : ${levelId}
        - Score : ${result.session.score}/100
        - Timing Error : ${metrics.timingErrorMs}ms
        - Stabilité : ${metrics.synchronization}%
        
        DATA :
        - Chroma Clarity : ${result.session.metrics.cleanliness}%
        - Posture Score : ${metrics.posture || 'N/A'}%

        INSTRUCTIONS PÉDAGOGIQUES :
        1. "Root Cause Analysis" : Ne dis pas "tu es en retard", dis "ton poignet est trop rigide à cause de X".
        2. "Transfer Test" : Donne un exercice spécifique à faire SANS l'application.
        3. "Cognitive Load" : Si le score est < 50, simplifie radicalement les termes techniques.
        
        LANGUE : ${lang === 'fr' ? 'Français' : 'Anglais'}.
    `;

    const schema = {
        type: Type.OBJECT,
        properties: {
            insight: { type: Type.STRING, description: "Verdict direct sur la cause motrice." },
            focus: { type: Type.STRING, description: "L'articulation ou le muscle prioritaire." },
            root_cause_analysis: { type: Type.STRING, description: "Analyse forensic du geste." },
            learning_outcome: { type: Type.STRING, description: "Compétence acquise après ce match." },
            exercise: {
                type: Type.OBJECT,
                properties: {
                    id: { type: Type.STRING },
                    name: { type: Type.STRING },
                    rules: { type: Type.ARRAY, items: { type: Type.STRING } },
                    duration: { type: Type.STRING }
                },
                required: ["id", "name", "rules", "duration"]
            },
            protocol: {
                type: Type.OBJECT,
                properties: {
                    priority_focus_id: { type: Type.STRING, enum: ['TIMING', 'HARMONY', 'CLEANLINESS', 'POSTURE'] },
                    remediation_steps: { type: Type.ARRAY, items: { type: Type.STRING } },
                    transfer_test_instruction: { type: Type.STRING, description: "Comment valider ce progrès sans l'app ?" }
                },
                required: ["priority_focus_id", "remediation_steps", "transfer_test_instruction"]
            }
        },
        required: ["insight", "focus", "root_cause_analysis", "exercise", "protocol"]
    };

    const data = await NeuralProxy.generateStructuredContent<any>({
        model: 'gemini-3-flash-preview',
        prompt,
        schema
    });

    return { summary: {
        ...data,
        ui_hint: data.root_cause_analysis
    }};
};
